import { publishJobArtifactsToBridge } from '../../features/search-settings/gateway/publishJobArtifactsToBridge';
import { ADMIN_RUNTIME_COMMANDS, createAdminRuntime } from './adminRuntime';
import { normalizeRuntimeSaveOutcome } from './saveOutcome';
import {
  normalizeAnalyticsReport,
  PLUGIN_ANALYTICS_ACTIONS,
} from './runtimeAnalytics';
import { prepareRecentAnalyticsDateRange } from '../../util/appUtils';
import { prepareStorefrontLanguage } from './prepareStorefrontLanguage';

const POLL_INTERVAL_MS = 3000;
// Gateway allows five minutes for a cold webpack build. The browser waits one
// additional minute so it can observe the terminal job state instead of timing
// out just before the server does.
const POLL_TIMEOUT_MS = 360000;
const CHECKPOINT_TIMEOUT_MS = 30000;
const LIVE_SET_ENABLED_TIMEOUT_MS = 30000;
const ONBOARDING_COMPLETE_TIMEOUT_MS = 30000;

function requireMethod(client, methodName, label) {
  if (!client || typeof client[methodName] !== 'function') {
    throw new Error(`${label} is not available. Reopen the embedded admin and try again.`);
  }
  return client[methodName].bind(client);
}

function extractJobId(response) {
  return response?.job_id || response?.jobId || response?.job?._id || response?.job?.jobId;
}

function assertSaveSucceeded(result) {
  if (result?.success !== false
    && (!Array.isArray(result?.errors) || result.errors.length === 0)) return;
  const first = result?.errors?.[0];
  throw new Error(first?.message || first?.error || 'Settings validation failed.');
}

function normalizeSaveResult(result, config) {
  return normalizeRuntimeSaveOutcome(result, config);
}

function stableSerialize(value) {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map(key => (
      `${JSON.stringify(key)}:${stableSerialize(value[key])}`
    )).join(',')}}`;
  }
  return JSON.stringify(value);
}

function comparableSettings(value) {
  const config = JSON.parse(JSON.stringify(value));
  // `mode` identifies the gateway storage lane, not a search setting. The
  // same published configuration is returned as `preview` from one lane and
  // `production` from the other, so including it makes every successful
  // publish look stale.
  if (config && typeof config === 'object' && !Array.isArray(config)) {
    delete config.mode;
    delete config._editorState;
  }
  return config;
}

function exactJsonMatch(left, right) {
  try {
    return stableSerialize(comparableSettings(left))
      === stableSerialize(comparableSettings(right));
  } catch (_error) {
    return false;
  }
}

async function writeCheckpoint(bridgeClient, checkpoint, value) {
  const send = requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');
  const response = await send(
    'onboarding.checkpoint',
    { checkpoint, value },
    CHECKPOINT_TIMEOUT_MS,
  );
  if (response?.result === 'error') {
    throw new Error(response.error || `Failed to save ${checkpoint} checkpoint`);
  }
}

async function sendBridgeAction(bridgeClient, command, payload = {}, timeout = 30000) {
  const send = requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');
  const response = await send(command, payload, timeout);
  if (response?.result === 'error') {
    throw new Error(response.error || `Plugin bridge action ${command} failed`);
  }
  return response;
}

async function reconcilePublishedSettings({
  gatewayClient,
  bridgeClient,
  shopId,
  publishedSettingsSnapshot,
}) {
  let preview = null;
  let production = null;
  let verificationError = null;
  try {
    const load = requireMethod(gatewayClient, 'loadSearchExperienceConfig', 'Gateway settings');
    [preview, production] = await Promise.all([
      load(shopId, 'preview'),
      load(shopId, 'production'),
    ]);
  } catch (error) {
    verificationError = error?.message || 'Settings verification failed';
  }

  const clean = !verificationError
    && exactJsonMatch(preview, publishedSettingsSnapshot)
    && exactJsonMatch(production, publishedSettingsSnapshot);
  if (clean) return { publishedClean: true, verificationError: null };

  await sendBridgeAction(bridgeClient, 'settings.mark_dirty', {});
  return {
    publishedClean: false,
    verificationError: verificationError
      || 'Production was updated, but a newer draft still needs to be published.',
  };
}

async function copyPreviewToProduction(gatewayClient, shopId) {
  const load = requireMethod(gatewayClient, 'loadSearchExperienceConfig', 'Gateway settings');
  const save = requireMethod(gatewayClient, 'saveSearchExperienceConfig', 'Gateway settings');
  const config = await load(shopId, 'preview');
  if (!config || typeof config !== 'object') {
    throw new Error('Gateway preview settings are not available.');
  }
  const result = await save(shopId, config, 'production');
  assertSaveSucceeded(result);
  return config;
}

async function runBundleJob({
  gatewayClient,
  bridgeClient,
  shopId,
  operation,
  methodName,
  target,
}) {
  const start = requireMethod(gatewayClient, methodName, 'Gateway bundle operation');
  const poll = requireMethod(gatewayClient, 'pollJobStatus', 'Gateway bundle polling');
  const response = await start(shopId);
  const jobId = extractJobId(response);
  if (!jobId) throw new Error(`${operation} did not return a job id`);

  if (operation === 'preview') {
    await writeCheckpoint(bridgeClient, 'preview_build_job_id', jobId);
  }

  const job = await poll(jobId, {
    intervalMs: POLL_INTERVAL_MS,
    timeoutMs: POLL_TIMEOUT_MS,
  });
  if (job?.status === 'failed' || job?.status === 'error') {
    throw new Error(job.error || `${operation} bundle operation failed`);
  }

  const publication = await publishJobArtifactsToBridge({
    gatewayClient,
    bridgeClient,
    jobId,
    target,
  });

  let publicationTruth = {};
  if (operation === 'production') {
    publicationTruth = await reconcilePublishedSettings({
      gatewayClient,
      bridgeClient,
      shopId,
      publishedSettingsSnapshot: publication.artifacts.settingsConfig,
    });
  }

  if (operation === 'preview') {
    await writeCheckpoint(bridgeClient, 'preview_built', true);
  }

  return {
    success: true,
    status: publicationTruth.publishedClean === false ? 'stale' : 'success',
    operation,
    jobId,
    ...publicationTruth,
  };
}

async function enableLive(bridgeClient) {
  const send = requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');
  const response = await send(
    'live.set_enabled',
    { enabled: true },
    LIVE_SET_ENABLED_TIMEOUT_MS,
  );
  if (response?.result === 'error') {
    throw new Error(response.error || 'Plugin rejected live status update');
  }
  if (!response?.live || typeof response.live !== 'object') {
    throw new Error('Plugin live status update did not return live state');
  }

  try {
    await send('onboarding.complete', {}, ONBOARDING_COMPLETE_TIMEOUT_MS);
  } catch (error) {
    // Older plugins may not implement this optional post-publish checkpoint.
  }
}

async function enrichOperatorStateWithIdentity(send, response) {
  try {
    const identityResponse = await send('gateway.identity.get', {});
    if (identityResponse?.result === 'error') return response;
    const identity = identityResponse?.identity || identityResponse?.data?.identity || {};
    const state = response?.state && typeof response.state === 'object'
      ? response.state
      : response;
    if (!state || typeof state !== 'object') return response;
    const context = state.context || {};
    const enrichedState = {
      ...state,
      context: {
        ...context,
        platform: identity.platform || context.platform || null,
        module_version: identity.moduleVersion
          || identity.module_version
          || context.module_version
          || null,
      },
    };
    return response?.state && typeof response.state === 'object'
      ? { ...response, state: enrichedState }
      : enrichedState;
  } catch (_error) {
    return response;
  }
}

export function createPluginAdminRuntime({
  gatewayClient,
  bridgeClient,
  artifactMutationEnabled = true,
}) {
  let operatorStateRequest = null;

  const loadOperatorState = async () => {
    const send = requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge');
    const onboardingState = await send('onboarding.state', {});
    if (onboardingState?.result !== 'error') {
      return enrichOperatorStateWithIdentity(send, onboardingState);
    }
    const bootstrap = await send('app.bootstrap', {});
    return enrichOperatorStateWithIdentity(send, bootstrap);
  };

  const handlers = {
    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD]: ({ shopId, mode }) => (
      requireMethod(gatewayClient, 'loadSearchExperienceConfig', 'Gateway settings')(shopId, mode)
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD_DEFAULTS]: ({ shopId, experience, mode }) => (
      requireMethod(gatewayClient, 'loadSearchExperienceDefaults', 'Gateway settings')(
        shopId,
        experience,
        mode,
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_SAVE_DRAFT]: async ({ shopId, config, mode }) => {
      const result = await requireMethod(
        gatewayClient,
        'saveSearchExperienceConfig',
        'Gateway settings',
      )(
        shopId,
        config,
        mode,
      );
      return normalizeSaveResult(result, config);
    },

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DESCRIBE]: ({ shopId }) => (
      requireMethod(gatewayClient, 'describeSearchExperienceConfig', 'Gateway settings')(shopId)
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COMPONENTS]: ({ shopId }) => (
      requireMethod(gatewayClient, 'getSearchComponents', 'Gateway settings')(shopId)
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COPY_TO_PRODUCTION]: async ({ shopId }) => ({
      config: await copyPreviewToProduction(gatewayClient, shopId),
    }),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED]: async () => {
      await sendBridgeAction(bridgeClient, 'settings.mark_dirty');
      await sendBridgeAction(bridgeClient, 'onboarding.invalidatePreview');
      return { success: true };
    },

    [ADMIN_RUNTIME_COMMANDS.ONBOARDING_MODE_SELECTED]: async ({ selectedMode }) => {
      await writeCheckpoint(bridgeClient, 'mode_selected', true);
      await writeCheckpoint(bridgeClient, 'selected_experience', selectedMode);
      return { success: true };
    },

    [ADMIN_RUNTIME_COMMANDS.ONBOARDING_INTEGRATION_CONFIGURED]: async () => {
      await writeCheckpoint(bridgeClient, 'integration_step_completed', true);
      await sendBridgeAction(bridgeClient, 'settings.mark_dirty');
      await sendBridgeAction(bridgeClient, 'onboarding.invalidatePreview');
      return { success: true };
    },

    [ADMIN_RUNTIME_COMMANDS.LIVE_SET_ENABLED]: async ({ enabled }) => {
      const response = await sendBridgeAction(
        bridgeClient,
        'live.set_enabled',
        { enabled },
        LIVE_SET_ENABLED_TIMEOUT_MS,
      );
      if (!response?.live || typeof response.live !== 'object') {
        throw new Error('Plugin live status update did not return live state');
      }
      return response.live;
    },

    [ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW]: async ({ shopId }) => {
      await prepareStorefrontLanguage({ bridgeClient, gatewayClient, shopId });
      return runBundleJob({
        gatewayClient,
        bridgeClient,
        shopId,
        operation: 'preview',
        methodName: 'buildPreviewBundle',
        target: 'preview',
      });
    },

    [ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE]: async ({ shopId }) => {
      await copyPreviewToProduction(gatewayClient, shopId);
      const result = await runBundleJob({
        gatewayClient,
        bridgeClient,
        shopId,
        operation: 'production',
        methodName: 'buildAndPromoteBundle',
        target: 'production',
      });
      await enableLive(bridgeClient);
      return result;
    },

    [ADMIN_RUNTIME_COMMANDS.ANALYTICS_REPORT]: async ({
      reportType,
      limit = 50,
      from = null,
      to = null,
      timeframeDays = null,
    }) => {
      const action = PLUGIN_ANALYTICS_ACTIONS[reportType];
      if (!action) throw new Error(`Unknown analytics report "${reportType}".`);
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      const payload = { limit };
      if (range.from) payload.from = range.from;
      if (range.to) payload.to = range.to;
      const response = await sendBridgeAction(bridgeClient, action, payload);
      return normalizeAnalyticsReport(response?.data, reportType);
    },

    [ADMIN_RUNTIME_COMMANDS.OPERATOR_STATE]: () => {
      if (!operatorStateRequest) {
        operatorStateRequest = loadOperatorState().finally(() => {
          operatorStateRequest = null;
        });
      }
      return operatorStateRequest;
    },

    [ADMIN_RUNTIME_COMMANDS.INDEXING_STATUS]: () => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'onboarding.state',
        {},
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_HISTORY]: ({ limit = 30 }) => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'indexing.history',
        { limit },
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_START]: () => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'indexing.start',
        {},
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_MARK_REQUIRED]: () => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'indexing.mark_required',
        {},
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_KICK]: ({ job_id: jobId }) => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'index.kick',
        { job_id: jobId },
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_RESTART_STALE]: ({ job_id: jobId }) => (
      requireMethod(bridgeClient, 'sendMessageWithResponse', 'Plugin bridge')(
        'index.restart_stale',
        { job_id: jobId },
      )
    ),

    [ADMIN_RUNTIME_COMMANDS.PREVIEW_RESOLVE_URL]: ({ url }) => (
      requireMethod(bridgeClient, 'getFinalUrl', 'Plugin preview resolver')(url)
    ),
  };

  if (!artifactMutationEnabled) {
    delete handlers[ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW];
    delete handlers[ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE];
  }

  return createAdminRuntime({ productId: 'plugin', handlers });
}
